import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import { Loader2 } from 'lucide-react'
import { ReportFileFormat } from '@/api/model'
import type { EmployeeUserResponse } from '@/api/model'
import { axiosInstance } from '@/api/axios-instance'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { notify } from '@/lib/notify'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  serviceModuleId: number
  /**
   * Without `employee`: export of **all** employees of the module (the list is loaded here).
   * With `employee`: export only for this employee.
   */
  employee?: EmployeeUserResponse
}

const FORMAT_EXTENSION: Record<ReportFileFormat, string> = {
  [ReportFileFormat.XLSX]: 'xlsx',
  [ReportFileFormat.CSV]: 'csv',
  [ReportFileFormat.PDF]: 'pdf',
}

/** Unwrap the query response from the axios wrapper if needed (hooks return the full response). */
function unwrap<T>(data: unknown): T | undefined {
  return ((data as { data?: T })?.data ?? data) as T | undefined
}

const now = new Date()

/**
 * Export dialog of the time tracking (year + month + format → file download). Without
 * `employee` all module employees are exported, with `employee` only that one.
 * Mirrors `AbsenceExportDialog`; file download goes via `axiosInstance` with
 * `responseType: 'blob'` (allowed exception, see conventions).
 */
export function TimeTrackingExportDialog({ open, onOpenChange, serviceModuleId, employee }: Props) {
  const { t } = useTranslation(['service-modules-timetracking', 'service-modules', 'common', 'filters'])
  const months = t('dates.months', { ns: 'common', returnObjects: true }) as string[]
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [format, setFormat] = useState<ReportFileFormat>(ReportFileFormat.XLSX)
  const [loading, setLoading] = useState(false)

  // "All": load the module's employee list (only in all-mode + open).
  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(serviceModuleId, {
    query: { enabled: open && !employee },
  })
  const allEmployeeIds = useMemo(
    () => (unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? []).map((e) => e.id).filter((id): id is string => !!id),
    [employeesQuery.data],
  )

  const employeeIds = employee ? [employee.id].filter((id): id is string => !!id) : allEmployeeIds
  const title = employee
    ? t('timetracking.export.titleEmployee', { name: `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || t('dropdown.employeeFallback', { ns: 'filters' }) })
    : t('timetracking.export.titleAll')

  async function handleExport() {
    if (employeeIds.length === 0) {
      notify.warning(t('shared.export.noEmployees', { ns: 'service-modules' }))
      return
    }
    setLoading(true)
    try {
      // Whole month as a UTC range. The end is the last day 23:59:59.999 - at 00:00 the
      // backend filter (StartTimestamp <= endDate) would drop that whole day (BON-1975).
      const startDate = new Date(Date.UTC(year, month - 1, 1)).toISOString()
      const endDate = new Date(Date.UTC(year, month, 0, 23, 59, 59, 999)).toISOString()

      const res = await axiosInstance.post(
        `/api/companies/current/servicemodules/timetracking/${serviceModuleId}/timetracking/reports`,
        { employeeIds, startDate, endDate, fileFormat: format },
        { responseType: 'blob' },
      )

      const url = window.URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `zeiterfassung_${year}_${String(month).padStart(2, '0')}.${FORMAT_EXTENSION[format]}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)

      onOpenChange(false)
    } catch (error) {
      notify.apiError(error, t('timetracking.export.exportError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tt-export-year">{t('shared.export.year', { ns: 'service-modules' })}</Label>
            <Input
              id="tt-export-year"
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('shared.export.month', { ns: 'service-modules' })}</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((label, index) => (
                  <SelectItem key={label} value={String(index + 1)}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('shared.export.format', { ns: 'service-modules' })}</Label>
            <Select value={format} onValueChange={(v) => setFormat(v as ReportFileFormat)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ReportFileFormat.XLSX}>XLSX</SelectItem>
                <SelectItem value={ReportFileFormat.CSV}>CSV</SelectItem>
                <SelectItem value={ReportFileFormat.PDF}>PDF</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button onClick={handleExport} disabled={loading}>
            {loading && <Loader2 className="size-4 animate-spin" />}
            {t('shared.export.download', { ns: 'service-modules' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
